import { type FC } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

// common
import LogoAvatar from 'common/components/LogoAvatar';
import { KIOS_STORAGE } from 'api/API_App';
import KiosImage from 'modules/quick-search/components/KiosImage';

// screen
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import { getSupplierExtras } from 'modules/suppliers/helpers/supplierExtras';

/** Ảnh KIOS có chỗ trả URL đầy đủ, có chỗ trả đường dẫn tương đối. */
const storageUrl = (path?: string | null): string => {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  return `${(KIOS_STORAGE || '').replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
};

// App dùng breakpoint tuỳ biến (mobile/tablet/laptop/desktop), KHÔNG có xs/md mặc định.
// 2 cột chỉ bật từ laptop (1024px); màn nhỏ hơn xếp dọc để mã CĐS dài không bị wrap xấu.
const grid2 = {
  display: 'grid',
  gridTemplateColumns: { mobile: '1fr', laptop: '1fr 1fr' },
  gap: 2,
} as const;

const LabeledValue: FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
  </Box>
);

/**
 * Hai khối đứng ĐẦU trang chi tiết HSNL NCC, dùng chung cho Quick Search và QR NCC TPAT:
 * "Thông tin NCC tham chiếu" + "Thông tin cơ bản HSNL".
 *
 * Đặt ngoài TabsView (không nằm trong tab "Thông tin chung") theo đúng thiết kế — nhờ vậy chuyển
 * sang tab "Loại hàng hoá" vẫn còn thấy đang xem hồ sơ của ai.
 */
const SupplierRefCards: FC = () => {
  const { formatMessage } = useIntl();
  const { generalDetails } = useTabsData();

  if (!generalDetails) return null;

  const ref = generalDetails.referencedSupplier;
  // Quick Search vẫn chỉ có ảnh/mô tả ở danh sách (localStorage). QR NCC TPAT thì BE đã ghép
  // hai trường này vào chi tiết — ưu tiên dữ liệu API, localStorage chỉ là dự phòng.
  const extras = getSupplierExtras(generalDetails.operationCode, generalDetails.id);
  const productImage = generalDetails.image || extras.image;
  const productDescription = generalDetails.description || extras.description;
  const label = (id: string) => formatMessage({ id: `supplier.refCards.${id}` });

  return (
    <Box sx={{ ...grid2, mb: 3 }}>
      {/* Thông tin NCC tham chiếu */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, color: 'primary.main', mb: 1.5 }}>
          {label('refTitle')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <LogoAvatar src={storageUrl(ref?.logo)} size={88} alt={ref?.name || ''} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>{ref?.name || '-'}</Typography>
            <Box sx={{ ...grid2, gap: 1 }}>
              <LabeledValue label={label('operationCode')} value={ref?.operationCode} />
              <Box />
              <LabeledValue
                label={label('businessRegistrationNumber')}
                value={ref?.businessRegistrationNumber}
              />
              <LabeledValue
                label={label('businessRegistrationType')}
                value={ref?.businessRegistrationTypeName}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Thông tin cơ bản HSNL */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, color: 'primary.main', mb: 1.5 }}>
          {label('hsnlTitle')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}
          >
            <KiosImage
              src={storageUrl(productImage)}
              alt={label('productImage')}
              fallbackIconSize={40}
              sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1 }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {label('productImage')}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontSize: 13, mb: 0.5 }}>
              {label('productDescription')}
              <Box component="span" sx={{ color: 'error.main' }}>
                {' *'}
              </Box>
            </Typography>
            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                px: 1.5,
                py: 1,
                minHeight: 96,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {productDescription || ''}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SupplierRefCards;
